import React, { useEffect, useState } from 'react';
import clsx from 'clsx';

import { calculatePrices } from 'frontend/js/helpers';

import { ILiquidSellingPlan, ILiquidSellingPlanGroups, ILiquidProduct} from '@src/types/product';
interface BundleProduct {
  id: number;
  handle: string;
  title: string;
  price: string;
  selected_or_first_available_variant_id: string;
  selling_plan_groups: ILiquidSellingPlanGroups;
  metafields: any;
}

interface Bundle {
  most_popular: string;
  product_name: string;
  products: BundleProduct[];
  single_item_price: string;
}

interface Props {
  product: BundleProduct;
  bundle: Bundle;
  selectedOption: number;
  index: number;
  currentProduct: ILiquidProduct;
  isLoading: boolean;
  isInTile?: boolean;
  setIsLoading: (loading: boolean) => void;
  onClick: (productData: any) => void;
  type?: 'one-time' | 'subscription';
  sellingPlanId?: number;
  translations?: any;
  onRender?: (index: number) => void;
}

interface Prices {
  productPrice: string;
  savings: number;
  singlePrice: number;
  quantityInBundle: number;
}

export const ProductBundleItem = ({
  product,
  bundle,
  selectedOption,
  index,
  onClick,
  currentProduct,
  isLoading,
  isInTile,
  setIsLoading,
  type = 'one-time',
  sellingPlanId,
  translations = {},
  onRender,
}: Props) => {
  const [prices, setPrices] = useState<Prices | null>(null);

  const bundleQuantity = Number(product.metafields.custom['product-z'] || index || 0);
 
  // Use translations for bundle quantities
  const bundleQuantities = [
    translations.single || 'Single',
    translations.double || 'Twin',
    translations.triple || 'Triple',
    translations.quadruple || 'Quadruple',
    translations.five || 'Five',
    translations.six || 'Six'
  ];

  function handleOneTimeOptionClick(product, prices) {
    if (onClick) onClick({ product, prices });
  }

 

  useEffect(() => {
    async function getPrices() {
      let pricesData: Prices | null = null;

      if (sellingPlanId) {
        let sellingPlan: ILiquidSellingPlan | undefined;
        if (product.selling_plan_groups?.[0]?.selling_plans) {
          const productPlans = product.selling_plan_groups[0].selling_plans;
          sellingPlan = productPlans.find(plan => plan.id == sellingPlanId);
        }

        if (!sellingPlan) {
          for (const bundleProduct of bundle.products) {
            if (bundleProduct.id !== product.id) {
              const plans = bundleProduct.selling_plan_groups?.[0]?.selling_plans || [];
              sellingPlan = plans.find(plan => plan.id == sellingPlanId);
              if (sellingPlan) break;
            }
          }
        }

       
        
        const discountValue = sellingPlan?.price_adjustments[0]?.value;
        
        if (!discountValue) {
          console.error(sellingPlanId, 'No discount value found for selling plan');
          return;
        }

        pricesData = {
          productPrice: (Math.round(((parseInt(product.price) / 100) * (1 - discountValue / 100)) * 100) / 100).toFixed(2),
          savings: discountValue,
          singlePrice: Number(product.price) / bundleQuantity, // Need to show original price thus the formula
          quantityInBundle: bundleQuantity
        };

      } else {
        const result = await calculatePrices(
          product,
          bundle.single_item_price,
          bundleQuantity,
          product.metafields.custom.product_related_discount_codes
        );
        
        if (result) {
          pricesData = {
            ...result,
            productPrice: result.productPrice as string,
            savings: result.savings
          };
        }
      }

      setPrices(pricesData);


    }

    getPrices();
  }, [sellingPlanId]);

  // Notify parent when this item is actually rendered (prices is not null)
  useEffect(() => {
      if (prices && onRender) {
        onRender(index);
        
      }
      if (selectedOption == product.id) {
        handleOneTimeOptionClick(product, prices);
      }
  }, [prices, onRender, index]);

  if (!prices) return null;

  return (
    <div
      className={clsx('product-selling-groups__option', {
        'product-selling-groups__option--active': selectedOption == product.id
      })}
      onClick={() => {
        if (product.handle !== currentProduct?.handle) {
          if(!isInTile) setIsLoading(true);
        }
        handleOneTimeOptionClick(product, prices);
      }}
      style={{ position: 'relative' }}
    >
      {isLoading && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(255, 255, 255, 1)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 10
        }}>
          <div style={{
            width: '30px',
            height: '30px',
            border: '4px solid #f3f3f3',
            borderTop: '4px solid #3498db',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      )}
      {String(product.id) == bundle.most_popular && (
        <div className='product-selling-groups__option__most-popular b5'>Most Popular</div>
      )}

      <div className='b3'>{bundleQuantities[bundleQuantity - 1]}</div>

      <div className='flex gap-x-2 gap-y-1 flex-wrap justify-end py-1'>
        <span className='b3--bold'>${prices?.productPrice}</span>

        {!!prices?.savings && (
          <div className='savings-badge b4--bold'>
            <span>SAVE</span>
            <span>{Math.round(prices.savings)}%</span>
          </div>
        )}
      </div>
    </div>
  );
};
